/**
 * Homepage infinite discovery.
 * Shopify-native pagination + Section Rendering API.
 * Curated collection pages append first. After that collection is exhausted,
 * voor-je-hausje continues the same feed. Inspiration stays on curated page 1.
 */

/** Same breakpoint as assets/scroll-container.js. Desktop scrolls .page-wrapper, not the document. */
const DESKTOP_SCROLL = typeof window !== 'undefined' ? window.matchMedia('(min-width: 990px)') : null;

function discoveryIntersectionRoot() {
  if (!DESKTOP_SCROLL?.matches) return null;
  return document.querySelector('.page-wrapper');
}

const ROOT_SELECTOR = '[data-hausje-infinite-discovery]';
const FEED_SELECTOR = '[data-hausje-discovery-feed]';
const ITEM_SELECTOR = '[data-hausje-discovery-item]';
const INSPIRATION_SELECTOR = '.hausje-discovery-grid__item--inspiration';
const EDITORIAL_SELECTOR = '[data-hausje-editorial-card]';
const EVENT_APPENDED = 'hausje:discovery-appended';

/** Consecutive all-duplicate pages fetched before yielding back to the scroll trigger. */
export const EMPTY_PAGE_BUDGET = 6;

/**
 * @param {string} sectionId
 * @param {number} page
 * @param {string} [baseHref]
 * @param {string} [phase]
 * @returns {string}
 */
export function buildNextPageSectionUrl(sectionId, page, baseHref = '/', phase = 'curated') {
  const url = new URL(baseHref, 'https://hausje.example');
  url.searchParams.set('page', String(page));
  if (phase === 'catalog') {
    url.searchParams.set('view', 'hausje-catalog');
    const catalogSection = String(sectionId || '').replace(/^shopify-section-/, '');
    if (catalogSection) url.searchParams.set('section_id', catalogSection);
    else url.searchParams.delete('section_id');
  } else {
    url.searchParams.delete('view');
    url.searchParams.set('section_id', String(sectionId || '').replace(/^shopify-section-/, ''));
  }
  url.searchParams.sort();
  return `${url.pathname}${url.search}`;
}

/**
 * @param {string} phase
 * @param {number} page
 */
export function discoveryRequestKey(phase, page) {
  return `${phase}:${page}`;
}

/**
 * @param {{ loading?: boolean, exhausted?: boolean, inFlightKey?: string | null, completedKeys?: Set<string>, phase?: string, page?: number | null }} state
 */
export function shouldFetchDiscoveryPage(state) {
  if (state.loading || state.exhausted || !state.phase || !state.page) return false;
  const key = discoveryRequestKey(state.phase, state.page);
  if (state.inFlightKey === key) return false;
  if (state.completedKeys && state.completedKeys.has(key)) return false;
  return true;
}

/**
 * Accept handles that are not already in the session feed.
 * Mutates `seen`. Order of first appearance is preserved.
 * @param {Set<string>} seen
 * @param {string[]} handles
 */
export function takeDiscoveryPage(seen, handles) {
  /** @type {string[]} */
  const accepted = [];
  for (const handle of handles) {
    if (!handle || seen.has(handle)) continue;
    seen.add(handle);
    accepted.push(handle);
  }
  return accepted;
}

/**
 * Decide the next collection page after one response.
 * A page with zero new products advances; it does not end the catalog.
 * The same phase+page cursor is refused so a stuck response cannot loop.
 * @param {{
 *   phase: string,
 *   page: number,
 *   nextPhase?: string | null,
 *   nextPage?: number | null,
 *   appendedCount: number,
 *   emptyStreak: number,
 *   emptyBudget?: number
 * }} input
 */
export function planDiscoveryCursor(input) {
  const budget = input.emptyBudget == null ? EMPTY_PAGE_BUDGET : input.emptyBudget;
  const nextPhase = input.nextPhase || '';
  const nextPage = input.nextPage;
  if (!nextPhase || nextPage == null || nextPage < 1) {
    return {
      exhausted: true,
      phase: input.phase,
      page: input.page,
      continueImmediately: false,
      emptyStreak: 0,
      blockedRepeat: false,
      yieldAfterEmptyBurst: false,
    };
  }
  if (nextPhase === input.phase && Number(nextPage) === Number(input.page)) {
    return {
      exhausted: true,
      phase: input.phase,
      page: input.page,
      continueImmediately: false,
      emptyStreak: input.emptyStreak,
      blockedRepeat: true,
      yieldAfterEmptyBurst: false,
    };
  }
  const emptyStreak = input.appendedCount > 0 ? 0 : input.emptyStreak + 1;
  const continueImmediately = input.appendedCount === 0 && emptyStreak <= budget;
  return {
    exhausted: false,
    phase: nextPhase,
    page: Number(nextPage),
    continueImmediately,
    emptyStreak: continueImmediately ? emptyStreak : 0,
    blockedRepeat: false,
    yieldAfterEmptyBurst: input.appendedCount === 0 && emptyStreak > budget,
  };
}

/**
 * A failed request keeps the cursor and the products already on screen.
 * @param {{ failCount: number, phase: string, page: number | null }} state
 */
export function noteDiscoveryFailure(state) {
  return {
    phase: state.phase,
    page: state.page,
    failCount: state.failCount + 1,
    exhausted: false,
    retrySamePage: true,
    loading: false,
  };
}

/**
 * @param {ParentNode} root
 * @returns {string[]}
 */
export function collectSeenHandles(root) {
  /** @type {string[]} */
  const handles = [];
  root.querySelectorAll(`${ITEM_SELECTOR}[data-product-handle]`).forEach((el) => {
    const handle = el.getAttribute('data-product-handle');
    if (handle) handles.push(handle);
  });
  return handles;
}

/**
 * @param {ParentNode} root
 * @returns {string[]}
 */
export function collectSeenEditorial(root) {
  /** @type {string[]} */
  const ids = [];
  root.querySelectorAll(`${EDITORIAL_SELECTOR}[data-hausje-after-product]`).forEach((el) => {
    const id = el.getAttribute('data-hausje-after-product');
    if (id) ids.push(id);
  });
  return ids;
}

/**
 * Walk the feed in DOM order. Product cards and later editorial cards append
 * together. Editorial cards are not products and never enter seenHandles.
 * The inspiration tile is never appended.
 *
 * @param {string} html
 * @param {Set<string>} seenHandles
 * @param {Set<string>} [seenEditorial]
 */
export function extractAppendableItems(html, seenHandles, seenEditorial = new Set()) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const feed = doc.querySelector(FEED_SELECTOR);
  const status = doc.querySelector('[data-hausje-infinite-status]');
  /** @type {HTMLElement[]} */
  const items = [];

  const acceptedHandles = new Set(seenHandles);
  const acceptedEditorial = new Set(seenEditorial);

  if (feed) {
    Array.from(feed.children).forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      if (node.matches(INSPIRATION_SELECTOR) || node.querySelector(INSPIRATION_SELECTOR)) return;
      if (node.matches(EDITORIAL_SELECTOR)) {
        const key = node.getAttribute('data-hausje-after-product') || '';
        if (!key || acceptedEditorial.has(key)) return;
        acceptedEditorial.add(key);
        node.querySelectorAll('img').forEach((img) => {
          if (!img.getAttribute('loading')) img.setAttribute('loading', 'lazy');
          img.removeAttribute('fetchpriority');
        });
        items.push(node);
        return;
      }
      if (!node.matches(ITEM_SELECTOR)) return;
      const handle = node.getAttribute('data-product-handle') || '';
      if (!handle || acceptedHandles.has(handle)) return;
      acceptedHandles.add(handle);
      node.querySelectorAll('img').forEach((img) => {
        if (!img.getAttribute('loading')) img.setAttribute('loading', 'lazy');
        img.removeAttribute('fetchpriority');
      });
      items.push(node);
    });
  }

  const nextPageRaw = status?.getAttribute('data-hausje-next-page');
  const totalRaw = status?.getAttribute('data-hausje-total-pages');
  const nextUrl = status?.getAttribute('data-hausje-next-url') || null;

  const renderedSection = doc.querySelector(ROOT_SELECTOR);

  return {
    items,
    sectionId: renderedSection?.getAttribute('data-hausje-section-id') || null,
    phase: status?.getAttribute('data-hausje-phase') || null,
    nextPhase: status?.getAttribute('data-hausje-next-phase') || null,
    nextPage: nextPageRaw ? Number(nextPageRaw) : null,
    totalPages: totalRaw ? Number(totalRaw) : null,
    nextUrl,
  };
}

/**
 * @param {HTMLElement} feed
 * @param {HTMLElement[]} items
 */
export function appendItems(feed, items) {
  if (!items.length) return 0;
  const present = new Set(collectSeenHandles(feed));
  const fragment = document.createDocumentFragment();
  let appended = 0;
  for (const item of items) {
    const handle = item.getAttribute('data-product-handle') || '';
    const editorial = item.hasAttribute('data-hausje-editorial-card');
    if (!editorial && handle) {
      if (present.has(handle)) continue;
      present.add(handle);
    }
    fragment.appendChild(document.importNode(item, true));
    appended += 1;
  }
  if (appended) feed.appendChild(fragment);
  return appended;
}

/**
 * @param {HTMLElement} root
 */
function syncSavedState(root) {
  const api = window.HausjeSaved;
  if (!api) return;
  if (typeof api.bindSaveButtons === 'function') api.bindSaveButtons(root);
  if (typeof api.syncAllSaveButtons === 'function') api.syncAllSaveButtons(root);
}

/**
 * @param {HTMLElement} root
 */
function enhanceRoot(root) {
  if (root.dataset.hausjeInfiniteBound === 'true') return;
  root.dataset.hausjeInfiniteBound = 'true';

  const feed = root.querySelector(FEED_SELECTOR);
  const status = root.querySelector('[data-hausje-infinite-status]');
  const sentinel = root.querySelector('[data-hausje-infinite-sentinel]');
  const loadingEl = root.querySelector('[data-hausje-infinite-loading]');
  const fallback = root.querySelector('[data-hausje-infinite-fallback]');
  if (!(feed instanceof HTMLElement) || !(status instanceof HTMLElement)) return;

  const sectionId = root.getAttribute('data-hausje-section-id') || '';
  const seen = new Set(collectSeenHandles(feed));
  const seenEditorial = new Set(collectSeenEditorial(feed));
  /** @type {Set<string>} */
  const completedKeys = new Set();

  let phase = status.getAttribute('data-hausje-phase') || 'curated';
  let currentPage = Number(status.getAttribute('data-hausje-current-page') || '1');
  let totalPages = Number(status.getAttribute('data-hausje-total-pages') || '1');
  let nextPhase = status.getAttribute('data-hausje-next-phase') || '';
  let nextPage = status.hasAttribute('data-hausje-next-page')
    ? Number(status.getAttribute('data-hausje-next-page'))
    : null;
  let nextUrl = status.getAttribute('data-hausje-next-url');
  let loading = false;
  /** @type {string | null} */
  let inFlightKey = null;
  let exhausted = !nextPage || !nextPhase;
  let failCount = 0;
  let emptyStreak = 0;
  let catalogSectionId = '';
  /** @type {IntersectionObserver | null} */
  let observer = null;

  if (phase && currentPage) completedKeys.add(discoveryRequestKey(phase, currentPage));

  const setLoading = (on) => {
    loading = on;
    if (loadingEl instanceof HTMLElement) loadingEl.hidden = !on;
    root.setAttribute('data-hausje-infinite-loading', on ? 'true' : 'false');
  };

  const setExhausted = () => {
    exhausted = true;
    nextPage = null;
    nextPhase = '';
    nextUrl = null;
    status.removeAttribute('data-hausje-next-page');
    status.removeAttribute('data-hausje-next-phase');
    status.removeAttribute('data-hausje-next-url');
    if (fallback instanceof HTMLElement) fallback.hidden = true;
    if (observer && sentinel) observer.unobserve(sentinel);
    root.setAttribute('data-hausje-infinite-exhausted', 'true');
    setLoading(false);
  };

  const showRetry = () => {
    if (!(fallback instanceof HTMLElement)) return;
    fallback.hidden = false;
    fallback.textContent = 'Opnieuw laden';
    fallback.setAttribute('data-hausje-infinite-retry', 'true');
  };

  const hideRetry = () => {
    if (!(fallback instanceof HTMLElement)) return;
    fallback.hidden = true;
    fallback.removeAttribute('data-hausje-infinite-retry');
    fallback.textContent = 'Laad meer';
  };

  const sentinelNear = () => {
    if (!(sentinel instanceof HTMLElement)) return false;
    const rect = sentinel.getBoundingClientRect();
    const scrollRoot = discoveryIntersectionRoot();
    const limit = scrollRoot
      ? scrollRoot.getBoundingClientRect().bottom + 240
      : window.innerHeight + 240;
    return rect.top < limit;
  };

  const continueIfNeeded = (decision, appended) => {
    if (decision.exhausted) return;
    const fillViewport = appended > 0 && sentinelNear();
    if (decision.continueImmediately || fillViewport) {
      loadNext();
      return;
    }
    if (decision.yieldAfterEmptyBurst && sentinelNear()) {
      emptyStreak = 0;
      window.setTimeout(() => {
        if (!exhausted && sentinelNear()) loadNext();
      }, 40);
    }
  };

  const loadNext = async () => {
    if (!shouldFetchDiscoveryPage({
      loading,
      exhausted,
      inFlightKey,
      completedKeys,
      phase: nextPhase,
      page: nextPage,
    }) || !sectionId) return;
    if (failCount >= 3) {
      showRetry();
      return;
    }

    const requestPhase = nextPhase;
    const requestPage = nextPage;
    const requestKey = discoveryRequestKey(requestPhase, requestPage);
    inFlightKey = requestKey;
    setLoading(true);
    hideRetry();
    const url = buildNextPageSectionUrl(
      requestPhase === 'catalog' ? catalogSectionId : sectionId,
      requestPage,
      window.location.href,
      requestPhase
    );

    try {
      const response = await fetch(url, {
        headers: { Accept: 'text/html' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      const parsed = extractAppendableItems(html, seen, seenEditorial);
      if (requestPhase === 'catalog' && parsed.phase !== 'catalog') {
        throw new Error('catalog response did not switch collection');
      }
      completedKeys.add(requestKey);
      if (requestPhase === 'catalog' && parsed.sectionId) catalogSectionId = parsed.sectionId;

      for (const item of parsed.items) {
        const handle = item.getAttribute('data-product-handle');
        if (handle && !item.hasAttribute('data-hausje-editorial-card')) seen.add(handle);
        const editorialKey = item.getAttribute('data-hausje-after-product');
        if (item.hasAttribute('data-hausje-editorial-card') && editorialKey) seenEditorial.add(editorialKey);
      }

      const appended = appendItems(feed, parsed.items);
      syncSavedState(feed);
      feed.dispatchEvent(
        new CustomEvent(EVENT_APPENDED, {
          bubbles: true,
          detail: { count: appended, page: requestPage, phase: requestPhase, root: feed },
        })
      );

      phase = parsed.phase || requestPhase;
      currentPage = requestPage;
      status.setAttribute('data-hausje-phase', phase);
      status.setAttribute('data-hausje-current-page', String(currentPage));

      if (parsed.totalPages != null) {
        totalPages = parsed.totalPages;
        status.setAttribute('data-hausje-total-pages', String(totalPages));
      }

      const decision = planDiscoveryCursor({
        phase: requestPhase,
        page: requestPage,
        nextPhase: parsed.nextPhase,
        nextPage: parsed.nextPage,
        appendedCount: appended,
        emptyStreak,
      });
      emptyStreak = decision.emptyStreak;

      if (decision.exhausted) {
        setExhausted();
        if (inFlightKey === requestKey) inFlightKey = null;
        return;
      }

      nextPhase = decision.phase;
      nextPage = decision.page;
      nextUrl = parsed.nextUrl;
      status.setAttribute('data-hausje-next-phase', nextPhase);
      status.setAttribute('data-hausje-next-page', String(nextPage));
      if (nextUrl && nextPhase === requestPhase) status.setAttribute('data-hausje-next-url', nextUrl);
      else status.removeAttribute('data-hausje-next-url');
      if (fallback instanceof HTMLAnchorElement && nextUrl && nextPhase === requestPhase) {
        fallback.href = nextUrl;
        fallback.hidden = true;
      }

      failCount = 0;
      if (inFlightKey === requestKey) inFlightKey = null;
      setLoading(false);
      continueIfNeeded(decision, appended);
      window.requestAnimationFrame(() => {
        if (!exhausted && !loading && sentinelNear()) loadNext();
      });
    } catch {
      const failure = noteDiscoveryFailure({ failCount, phase: requestPhase, page: requestPage });
      failCount = failure.failCount;
      if (inFlightKey === requestKey) inFlightKey = null;
      setLoading(false);
      showRetry();
      // Preserve already-loaded products; do not clear the feed.
    }
  };

  if (fallback instanceof HTMLElement) {
    fallback.hidden = true;
    fallback.addEventListener('click', (event) => {
      if (fallback.getAttribute('data-hausje-infinite-retry') === 'true' || !exhausted) {
        event.preventDefault();
        failCount = 0;
        loadNext();
      }
    });
  }

  if (exhausted) {
    setExhausted();
    return;
  }

  const attachObserver = () => {
    if (!(sentinel instanceof HTMLElement) || typeof IntersectionObserver === 'undefined') return;
    if (observer) observer.disconnect();
    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) loadNext();
        }
      },
      { root: discoveryIntersectionRoot(), rootMargin: '240px 0px', threshold: 0 }
    );
    observer.observe(sentinel);
  };

  attachObserver();
  const previousRootListener = root._hausjeScrollRootListener;
  if (DESKTOP_SCROLL && typeof previousRootListener === 'function') {
    DESKTOP_SCROLL.removeEventListener('change', previousRootListener);
  }
  const onScrollRootChange = () => attachObserver();
  root._hausjeScrollRootListener = onScrollRootChange;
  DESKTOP_SCROLL?.addEventListener('change', onScrollRootChange);
}

function init() {
  document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
    if (root instanceof HTMLElement) enhanceRoot(root);
  });
  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (root instanceof HTMLElement) {
        root.dataset.hausjeInfiniteBound = '';
        enhanceRoot(root);
      }
    });
  });
}

if (typeof window !== 'undefined') {
  window.HausjeDiscoveryInfinite = {
    buildNextPageSectionUrl,
    collectSeenHandles,
    collectSeenEditorial,
    extractAppendableItems,
    appendItems,
    planDiscoveryCursor,
    takeDiscoveryPage,
    shouldFetchDiscoveryPage,
    noteDiscoveryFailure,
    EVENT_APPENDED,
  };
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
}
